import React, { useState } from 'react';
import { Button, Modal, Dropdown, Alert, Accordion, Tabs } from '../UI';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  Cog6ToothIcon,
  UserIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';

/**
 * Página de demostración de componentes UI
 * 
 * Muestra cómo usar los componentes de Tailwind + Headless UI
 * Accede desde: /admin/ui-demo (agregar ruta si deseas)
 */
const UIDemo = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAlert, setShowAlert] = useState(true);

  // Items para el dropdown
  const dropdownItems = [
    { label: 'Mi Perfil', icon: UserIcon, onClick: () => console.log('Perfil') },
    { label: 'Configuración', icon: Cog6ToothIcon, onClick: () => console.log('Config') },
    { divider: true },
    { label: 'Cerrar Sesión', icon: ArrowRightOnRectangleIcon, onClick: () => console.log('Logout'), danger: true },
  ];

  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Componentes UI - Tailwind + Headless UI
          </h1>
          <p className="mt-2 text-gray-600">
            Ejemplos de componentes para reemplazar Materialize CSS
          </p>
        </div>

        {/* Sección: Buttons */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Buttons</h2>
          
          <div className="space-y-4">
            {/* Variantes */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Variantes</h3>
              <div className="flex flex-wrap gap-3">
                <Button variant="primary">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="success">Success</Button>
                <Button variant="danger">Danger</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="outline">Outline</Button>
              </div>
            </div>

            {/* Tamaños */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Tamaños</h3>
              <div className="flex flex-wrap items-center gap-3">
                <Button size="sm">Pequeño</Button>
                <Button size="md">Mediano</Button>
                <Button size="lg">Grande</Button>
              </div>
            </div>

            {/* Con iconos */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Con iconos</h3>
              <div className="flex flex-wrap gap-3">
                <Button icon={PlusIcon}>Agregar</Button>
                <Button icon={PencilIcon} variant="secondary">Editar</Button>
                <Button icon={TrashIcon} variant="danger">Eliminar</Button>
                <Button icon={ArrowDownTrayIcon} iconPosition="right" variant="success">
                  Descargar
                </Button>
              </div>
            </div>

            {/* Estados */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Estados</h3>
              <div className="flex flex-wrap gap-3">
                <Button disabled>Deshabilitado</Button>
                <Button loading>Cargando</Button>
              </div>
            </div>
          </div>
        </section>

        {/* Sección: Alerts */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Alerts</h2>
          
          <div className="space-y-3">
            <Alert 
              type="info" 
              title="Información" 
              message="Este es un mensaje informativo para el usuario."
            />
            <Alert 
              type="success" 
              title="¡Éxito!" 
              message="La operación se completó correctamente."
              onClose={() => {}}
            />
            <Alert 
              type="warning" 
              title="Advertencia" 
              message="Hay algo que requiere tu atención."
            />
            <Alert 
              type="error" 
              title="Error" 
              message="Ocurrió un error al procesar la solicitud."
              onClose={() => {}}
            />
          </div>
        </section>

        {/* Sección: Dropdown */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Dropdown</h2>
          
          <div className="flex gap-4">
            <Dropdown 
              trigger="Opciones" 
              items={dropdownItems}
            />
            <Dropdown 
              trigger="Alineado derecha" 
              items={dropdownItems}
              align="right"
            />
          </div>
        </section>

        {/* Sección: Modal */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Modal</h2>
          
          <Button onClick={() => setIsModalOpen(true)}>
            Abrir Modal
          </Button>

          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title="Ejemplo de Modal"
            size="md"
          >
            <div className="space-y-4">
              <p className="text-gray-600">
                Este es un modal creado con Headless UI y estilizado con Tailwind CSS.
                Incluye animaciones, accesibilidad completa y es totalmente personalizable.
              </p>
              
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={() => setIsModalOpen(false)}>
                  Confirmar
                </Button>
              </div>
            </div>
          </Modal>
        </section>

        {/* Sección: Accordion */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Accordion</h2>
          
          <Accordion
            items={[
              {
                title: 'Sección 1: Información General',
                content: 'Este es el contenido de la primera sección. Puede contener texto, componentes o cualquier elemento React.',
                defaultOpen: true,
              },
              {
                title: 'Sección 2: Configuración',
                content: (
                  <div className="space-y-2">
                    <p>Aquí puedes agregar componentes personalizados:</p>
                    <Button size="sm" variant="secondary">Botón dentro del acordeón</Button>
                  </div>
                ),
              },
              {
                title: 'Sección 3: Preguntas Frecuentes',
                content: '¿Cómo funciona? Este acordeón usa Headless UI para la lógica y Tailwind para los estilos.',
              },
            ]}
          />
        </section>

        {/* Sección: Tabs */}
        <section className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Tabs</h2>
          
          <div className="space-y-8">
            {/* Tabs default */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Estilo Default</h3>
              <Tabs
                tabs={[
                  {
                    label: 'Inicio',
                    icon: HomeIcon,
                    content: <p className="text-gray-600">Contenido de la pestaña Inicio</p>,
                  },
                  {
                    label: 'Documentos',
                    icon: DocumentTextIcon,
                    badge: '3',
                    content: <p className="text-gray-600">Contenido de la pestaña Documentos</p>,
                  },
                  {
                    label: 'Reportes',
                    icon: ChartBarIcon,
                    content: <p className="text-gray-600">Contenido de la pestaña Reportes</p>,
                  },
                ]}
              />
            </div>

            {/* Tabs pills */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Estilo Pills</h3>
              <Tabs
                variant="pills"
                tabs={[
                  { label: 'Tab 1', content: <p className="text-gray-600">Contenido Tab 1</p> },
                  { label: 'Tab 2', content: <p className="text-gray-600">Contenido Tab 2</p> },
                  { label: 'Tab 3', content: <p className="text-gray-600">Contenido Tab 3</p> },
                ]}
              />
            </div>

            {/* Tabs boxed */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Estilo Boxed</h3>
              <Tabs
                variant="boxed"
                tabs={[
                  { label: 'Activo', content: <p className="text-gray-600">Usuarios activos</p> },
                  { label: 'Inactivo', content: <p className="text-gray-600">Usuarios inactivos</p> },
                  { label: 'Todos', content: <p className="text-gray-600">Todos los usuarios</p> },
                ]}
              />
            </div>
          </div>
        </section>

        {/* Código de ejemplo */}
        <section className="bg-gray-800 rounded-xl shadow-sm p-6 text-white">
          <h2 className="text-xl font-semibold mb-4">Cómo usar</h2>
          
          <pre className="text-sm overflow-x-auto">
            <code>{`import { Button, Modal, Dropdown, Alert } from '../Components/UI';
import { PlusIcon } from '@heroicons/react/24/outline';

// Button
<Button variant="primary" icon={PlusIcon}>
  Agregar
</Button>

// Modal
<Modal isOpen={isOpen} onClose={onClose} title="Mi Modal">
  Contenido del modal
</Modal>

// Alert
<Alert type="success" title="¡Éxito!" message="Operación completada" />

// Dropdown
<Dropdown 
  trigger="Menú" 
  items={[
    { label: 'Opción 1', onClick: () => {} },
    { label: 'Opción 2', onClick: () => {} },
  ]} 
/>`}</code>
          </pre>
        </section>
      </div>
    </div>
  );
};

export default UIDemo;
